import { defineComponent } from "@openuidev/lang-core";
import { z } from "zod";

// Reasoning widgets. See ../widgets.ts for the rules all widgets follow.
// None of them carries the expected grouping, order, term or mapping: the child shares
// their thinking and the tutor talks about it; the engine decides what comes next.

/** A short label the child moves around: a card, a step, a statement. */
const label = (max: number) => z.string().trim().min(1).max(max);

export const CategorySort = defineComponent({
  name: "CategorySort",
  description:
    "Cards the child sorts into 2 or 3 labelled groups, then shares with you. Use to classify (mammals or not, even or odd, solid or liquid). Give the cards in a mixed order; never say which group is right.",
  props: z.object({
    groups: z.array(label(32)).min(2).max(3),
    cards: z.array(label(40)).min(2).max(8),
    prompt: label(200).optional(),
  }),
  component: null,
});

export const SequenceOrder = defineComponent({
  name: "SequenceOrder",
  description:
    "Steps the child puts in order with move up and move down buttons, then shares. Use for procedures, timelines and life stages. Give the steps shuffled; never in the right order.",
  props: z.object({
    steps: z.array(label(80)).min(3).max(6),
    prompt: label(200).optional(),
  }),
  component: null,
});

export const PatternSequence = defineComponent({
  name: "PatternSequence",
  description:
    'A row of terms (numbers, letters or shape words) with one or more gaps written as "?", which the child fills in and shares. Use for "what comes next" and missing-term reasoning. Never include the missing terms anywhere.',
  props: z.object({
    terms: z
      .array(label(12))
      .min(3)
      .max(8)
      .refine((terms) => terms.includes("?"), { message: 'needs at least one "?" gap' })
      .refine((terms) => terms.some((term) => term !== "?"), {
        message: "needs at least one given term",
      }),
    prompt: label(200).optional(),
  }),
  component: null,
});

export const ClaimEvidence = defineComponent({
  name: "ClaimEvidence",
  description:
    'An argument builder: a claim and 2 to 6 statements the child marks as "because" (supports it), "but" (challenges it) or "not relevant", then shares. Use for evidence and critical thinking. Never say which statements support the claim.',
  props: z.object({
    claim: label(200),
    statements: z.array(label(160)).min(2).max(6),
    prompt: label(200).optional(),
  }),
  component: null,
});

export const reasoningWidgets = [
  CategorySort,
  SequenceOrder,
  PatternSequence,
  ClaimEvidence,
] as const;
